import { useMemo, useState } from "react";
import { Banner } from "react-migration/domains/sites";
import ACCOUNT from "react-migration/domains/sites/apollo/sites-api/queries/account.gql";
import VALIDATE_ATKINS_ORDER_INPUT from "react-migration/domains/sites/apollo/sites-api/queries/validateAtkinsOrderInput.gql";
import {
  AccountResponseData,
  ValidateAtkinsOrderInputResponse,
} from "react-migration/domains/sites/typings";
import {
  LandTechEndpoints,
  Stores,
  routedClientWithoutTypenames,
  useGlobalState,
  useTranslation,
} from "react-migration/lib";

import { formatToCurrency } from "react-migration/lib/util";
import { router } from "src/js/router/react-router";
import ROUTES from "src/js/router/routes";

import { useQuery } from "@apollo/client";
import { Button } from "@landtechnologies/components";

import { ConfirmPaymentModal } from "./ConfirmPaymentModal";
import { setDashboardTopUpRedirectUrl } from "../../../../../../js/stores/site/actions/setDashboardTopUpRedirectUrl";
import { logUtilitiesReportEvent } from "./logUtilitiesEvent";

export const OverviewContentStep = () => {
  const { t } = useTranslation();

  const [isConfirmPaymentModalOpen, setIsConfirmPaymentModalOpen] = useState<boolean>(false);
  const [showMapBufferBanner, setIsShowMapBufferBanner] = useState(true);

  const [{ locale }] = useGlobalState(Stores.user);
  const [{ utilityGeometry }] = useGlobalState(Stores.site);
  const [{ selection }] = useGlobalState(Stores.map);

  const {
    loading: accountLoading,
    error: accountError,
    data: accountData,
  } = useQuery<AccountResponseData>(ACCOUNT, {
    client: routedClientWithoutTypenames,
    context: {
      endpoint: LandTechEndpoints.Sites,
    },
    fetchPolicy: "network-only",
  });

  const {
    error: atkinsValidationError,
    loading: atkinsValidationLoading,
    data: atkinsValidationData,
  } = useQuery<ValidateAtkinsOrderInputResponse>(VALIDATE_ATKINS_ORDER_INPUT, {
    client: routedClientWithoutTypenames,
    context: {
      endpoint: LandTechEndpoints.Sites,
    },
    fetchPolicy: "network-only",
    variables: {
      atkinsOrderInput: {
        siteId: selection?.id,
        geometry: utilityGeometry,
      },
    },
  });

  const currentBalance = useMemo(() => {
    if (accountLoading || !accountData) {
      return 0;
    }
    return accountData.account.balance / 100;
  }, [accountData, accountLoading]);

  const hasSufficientBalance = currentBalance >= 720;
  const validationCheckPassed =
    atkinsValidationData?.validateAtkinsOrderInput.canAtkinsOrderBePlaced;
  const userCanPurchase = !accountLoading && !accountError && hasSufficientBalance;
  const siteIsValid = !atkinsValidationLoading && !atkinsValidationError && validationCheckPassed;
  const [purchaseButtonClicked, setPurchaseButtonClicked] = useState<boolean>(false);

  const onTopUpCreditsClicked = () => {
    logUtilitiesReportEvent({
      name: "Utilities Report - Balance Top Up Clicked",
    });
    setDashboardTopUpRedirectUrl(ROUTES.MAP);
    router.push({
      name: ROUTES.DASHBOARD_TOPUP,
    });
  };

  const openSampleReport = () => {
    logUtilitiesReportEvent({
      name: "Utilities Report - View Sample Report Button Clicked",
    });
    window.open(process.env.ATKINS_SAMPLE_REPORT_PDF_LINK, "_blank", "noreferrer,noopener");
  };

  const handlePurchaseButtonClicked = () => {
    setPurchaseButtonClicked(true);
    if (userCanPurchase && siteIsValid) {
      setIsConfirmPaymentModalOpen(true);
    }
    logUtilitiesReportEvent({
      name: "Utilities Report - Credit Payment Button Clicked",
    });
  };

  // This needs to broken down into smaller pieces / refactored, it's getting a bit too big
  return (
    <div className="atlas-flex atlas-flex-col atlas-h-full" data-testid="overview-step-content">
      <div className="atlas-text-lg atlas-font-bold atlas-leading-6 atlas-flex atlas-justify-between atlas-px-5 atlas-pt-5 atlas-pb-2">
        {t("sites.utility_report.title")}
        <div>
          {formatToCurrency({ amount: 600.0, currency: "GBP", locale })}
          <span className="atlas-text-xs atlas-font-normal atlas-ml-1">
            {t("sites.utility_report.excl_VAT")}
          </span>
        </div>
      </div>
      <div className="atlas-flex atlas-flex-col atlas-flex-grow atlas-overflow-y-auto atlas-h-0 atlas-px-5">
        <img
          src="utilities/UtilitiesReportImage.svg"
          alt="Utility Report Example"
          className="atlas-mt-2 atlas-w-full"
          loading="eager"
        />

        <Button.Secondary
          onClick={openSampleReport}
          data-testid="report-view-sample"
          className="atlas-gap-2 atlas-mx-auto atlas-my-4"
        >
          {t("sites.utility_report.view_sample_report_button")}
          <i className="icon-lt-external-link-line atlas-text-xl" />
        </Button.Secondary>

        {t("sites.utility_report.text")}
        <ul className="atlas-pl-4">
          <li className="atlas-py-3">{t("sites.utility_report.bullet_1")}</li>
          <li className="atlas-pb-3">{t("sites.utility_report.bullet_2")}</li>
          <li className="atlas-pb-3">{t("sites.utility_report.bullet_3")}</li>
        </ul>
      </div>
      <div className="atlas-border-t atlas-border-border-divider-subtle atlas-relative atlas-p-5">
        <div data-testid="current-balance-container">
          {accountLoading && t("sites.utility_report.loading_balance")}
          {userCanPurchase && (
            <Banner
              variant="success"
              message={t("sites.utility_report.current_balance", {
                currentBalance: (
                  <span className="atlas-font-bold">
                    {formatToCurrency({
                      amount: currentBalance,
                      currency: "GBP",
                    })}
                  </span>
                ),
              })}
            />
          )}
          {!userCanPurchase && !accountLoading && (
            <Banner
              variant="warning"
              message={
                <div>
                  <div className="atlas-font-bold">
                    {t("sites.utility_report.not_enough_balance_title")}
                  </div>
                  <div data-testid="not-enough-balance-message">
                    {t("sites.utility_report.not_enough_balance", {
                      currentBalance: (
                        <span className="atlas-font-bold">
                          {formatToCurrency({
                            amount: currentBalance,
                            currency: "GBP",
                          })}
                        </span>
                      ),
                    })}
                  </div>
                  <div>
                    {t("sites.utility_report.top_up_balance", {
                      clickHereLink: (
                        <button
                          className="atlas-border-0 atlas-bg-transparent atlas-outline-none atlas-font-bold atlas-text-content-link atlas-underline"
                          onClick={() => {
                            onTopUpCreditsClicked();
                          }}
                        >
                          {t("sites.utility_report.click_here")}
                        </button>
                      ),
                    })}
                  </div>
                </div>
              }
            />
          )}
        </div>
        <div className="atlas-flex atlas-justify-end atlas-pt-4">
          <Button.Primary
            data-testid="report-purchase-button"
            onClick={handlePurchaseButtonClicked}
            disabled={
              !!atkinsValidationError || !userCanPurchase || (!siteIsValid && purchaseButtonClicked)
            }
          >
            {t("sites.utility_report.purchase_button")}
          </Button.Primary>
        </div>

        {isConfirmPaymentModalOpen && (
          <ConfirmPaymentModal onClose={() => setIsConfirmPaymentModalOpen(false)} />
        )}

        <div className="atlas-absolute atlas-bottom-full atlas-left-0 atlas-right-0 atlas-px-5 atlas-pb-1">
          {showMapBufferBanner && (
            <Banner
              variant="info"
              message={t("sites.utility_report.the_white_dotted_area")}
              className="atlas-shadow atlas-mb-2"
              showIcon
              onClose={() => setIsShowMapBufferBanner(false)}
            />
          )}
          {!siteIsValid &&
            purchaseButtonClicked &&
            atkinsValidationData?.validateAtkinsOrderInput.errors.map((error) => (
              <Banner
                showIcon
                key={error}
                variant="error"
                message={t(`sites.utility_report.validation_error.${error.toLowerCase()}`)}
                className="atlas-shadow atlas-mb-2"
              />
            ))}
          {accountError && (
            <Banner
              showIcon
              variant="error"
              message={t("sites.letters.we_cant_display_your_balance_at_the_moment")}
              className="atlas-shadow atlas-mb-2"
            />
          )}
          {atkinsValidationError && (
            <Banner
              showIcon
              variant="error"
              message={t("sites.utility_report.validation_error.issue_validating_this_site")}
              className="atlas-shadow atlas-mb-2"
            />
          )}
        </div>
      </div>
    </div>
  );
};
